import { List } from 'immutable'
import React from 'react'
import { Updater, any, fromJSX, Widget } from 'widgets-for-react'
import { BlocksLayout } from './blocksLayout'
import { Block, blocksStateUpdaters } from './blocksState'
import { CtaBlockLayout } from './ctaBlockLayout'
import { expandBlockWidget } from './expandBlock/expandBlockWidget'
import { FeaturedContentBlockLayout } from './featuredContentBlockLayout'
import { LinkBlockLayout } from './linkBlockLayout'
import { LinkGridBlockLayout } from './linkGridBlockLayout'
import { QuoteBlockLayout } from './quoteBlockLayout'
import { relatedContentBlockWidget } from './relatedContentBlock/relatedContentBlockWidget'
import { TextBlockLayout } from './textBlockLayout'
import { TextImageBlockLayout } from './textImageBlockLayout'
import { CookiebarState, cookiebarStateUpdaters } from '../../cookiebar/cookiebarState'
import { getCookie } from '../../../utils/cookies'
import { MediaBlockLayout } from './mediaBlockLayout'
import { childDossiersBlockWidget } from './childDossiersBlock/childDossiersBlockWidget'
import { dossierArticlesBlockWidget } from './dossierArticlesBlock/dossierArticlesBlockWidget'
import { courseOverviewWidget } from './courseOverview/courseOverviewWidget'
import { allCourseGroupsBlockWidget } from './allCourseGroupsBlock/allCourseGroupsBlockWidget'
import { allCasesBlockWidget } from './allCasesBlock/allCasesBlockWidget'
import { childCourseGroupsBlockWidget } from './childCourseGroupsBlock/childCourseGroupsBlockWidget'
import { courseDetailsBlockWidget } from './courseDetailsBlock/courseDetailsBlockWidget'
import { allEventsBlockWidget } from './eventOverviewBlock/allEventsBlockWidget'
import { allSubscriptionsBlockWidget } from './allSubscriptionsBlock/allSubscriptionsBlockWidget'
import { DownloadBlockLayout } from './downloadsBlockLayout'
import { allNewsBlockWidget } from './allNewsBlock/allNewsBlockWidget'
import { IncompanyBlockLayout } from './incompanyBlockLayout'
import { relatedActualitiesBlockWidget } from './relatedActualitiesBlock/relatedActualitiesBlockWidget'
import { contactPersonBlockWidget } from './contactPersonBlock/contactPersonBlockWidget'
import { IframeBlockLayout } from './iframeBlockLayout'
import { allCourseCategoriesBlockWidget } from './allCourseCategoriesBlock/allCourseCategoriesBlockWidget'
import { courseSearchBarWidget } from './CourseSearchBar/courseSearchBarWidget'
import { knowledgeSearchBarWidget} from './KnowledgeSearchBar/knowledgeSearchBarWidget'
import { actualitySearchBarWidget } from './ActualitySearchBar/actualitySearchBarWidget'
import { Pages } from '../../routes/routesState'
import { logoSliderBarWidget } from './LogoSlider/logoSliderWidget'
import { AdvertisementBlockLayout } from './advertisementBlockLayout'
import { PageParams } from '../../../utils/routes'
import { setConsentCookie } from '../../../utils/datalayer.mapping'
import { eventDetailsBlockWidget } from './eventDetailsBlock/eventDetailsBlock.widget'

export const blocksWidget =
  (setCookiebarState: (_: Updater<CookiebarState>) => void, params?: PageParams<Pages['cmsPage']>) =>
  (blocks: List<Block>) =>
    any<Updater<List<Block>>>(
      blocks
        .map((block, index) =>
          blockSwitch(setCookiebarState,params)(block).map(blocksStateUpdaters.blockUpdater(index))
        )
        .toArray()
    ).wrapHTML((element) => <BlocksLayout children={element} />)

export const blockSwitch =
  (setCookiebarState: (_: Updater<CookiebarState>) => void, params?: PageParams<Pages['cmsPage']>) =>
  (block: Block): Widget<Updater<Block>> => {
    const videoCookieProps = {
      videoCookieAccepted: getCookie('video-cookies') === 'yes',
      acceptVideoCookie: () => {
        setConsentCookie('video-cookies', 'yes')
        setCookiebarState(cookiebarStateUpdaters.singleCookieUpdater('video-cookies', 'yes'))
      },
    }
    switch (block.kind) {
      case 'ctaBlock':
        return fromJSX(() => <CtaBlockLayout {...block} />)
      case 'expandBlock':
        return expandBlockWidget({
          ...block,
          ...videoCookieProps,
        }).map(blocksStateUpdaters.expandBlockStateUpdater)
      case 'featuredContentBlock':
        return fromJSX(() => <FeaturedContentBlockLayout {...block} />)
      case 'linkBlock':
        return fromJSX(() => <LinkBlockLayout {...block} />)
      case 'linkGridBlock':
        return fromJSX(() => <LinkGridBlockLayout {...block} />)
      case 'mediaBlock':
        return fromJSX(() => <MediaBlockLayout {...block} {...videoCookieProps} />)
      case 'quoteBlock':
        return fromJSX(() => <QuoteBlockLayout {...block} />)
      case 'textImageBlock':
        return fromJSX(() => <TextImageBlockLayout {...block} />)
      case 'textBlock':
        return fromJSX(() => <TextBlockLayout {...block} />)
      case 'eventDetailsBlock':
        return eventDetailsBlockWidget(block, params && params.page).map(blocksStateUpdaters.eventDetailsBlockStateUpdater)
      case 'downloadsBlock':
        return fromJSX(() => <DownloadBlockLayout {...block} />)
      case 'incompanyBlock':
        return fromJSX(() => <IncompanyBlockLayout />)
      case 'iframe':
        return fromJSX(() => <IframeBlockLayout {...block} />)
      case 'courseSearchBar':
        return courseSearchBarWidget(block).map(blocksStateUpdaters.courseSearchBarBlockUpdater)
      case 'knowledgeSearchBar':
        return knowledgeSearchBarWidget(block).map(blocksStateUpdaters.knowledgeSearchBarBlockUpdater)
      case 'contactPersonBlock':
        return contactPersonBlockWidget(block).map(blocksStateUpdaters.contactPersonBlockStateUpdater)
      case 'relatedContentBlock':
        return relatedContentBlockWidget(block).map(blocksStateUpdaters.relatedContentBlockStateUpdater)
      case 'childDossiersBlock':
        return childDossiersBlockWidget(block).map(blocksStateUpdaters.childDossiersBlockStateUpdater)
      case 'dossierArticlesBlock':
        return dossierArticlesBlockWidget(block).map(blocksStateUpdaters.dossierArticlesBlockStateUpdater)
      case 'courseOverviewBlock':
        return courseOverviewWidget(block).map(blocksStateUpdaters.courseGroupCoursesBlockStateUpdater)
      case 'allCourseGroupsBlock':
        return allCourseGroupsBlockWidget(block).map(blocksStateUpdaters.allCourseGroupsBlockStateUpdater)
      case 'allCourseCategoriesBlock':
        return allCourseCategoriesBlockWidget(block).map(blocksStateUpdaters.allCourseCategoriesBlockStateUpdater)
      case 'allCasesBlock':
        return allCasesBlockWidget(block).map(blocksStateUpdaters.allCasesBlockStateUpdater)
      case 'allSubscriptionsBlock':
        return allSubscriptionsBlockWidget(block).map(blocksStateUpdaters.allSubscriptionsBlockStateUpdater)
      case 'allNewsBlock':
        return allNewsBlockWidget(block).map(blocksStateUpdaters.allNewsBlockStateUpdater)
      case 'allEventsBlock':
        return allEventsBlockWidget(block).map(blocksStateUpdaters.allEventsBlockStateUpdater)
      case 'childCourseGroupsBlock':
        return childCourseGroupsBlockWidget(block).map(blocksStateUpdaters.childCourseGroupsBlockStateUpdater)
      case 'courseDetailsBlock':
        return courseDetailsBlockWidget(block, params && params.page).map(blocksStateUpdaters.courseDetailsBlockStateUpdater)
      case 'relatedActualitiesBlock':
        return relatedActualitiesBlockWidget(block).map(blocksStateUpdaters.relatedActualitiesBlockStateUpdater)
      case 'actualitySearchBar':
        return actualitySearchBarWidget(block).map(blocksStateUpdaters.actualitySearchBarBlockUpdater)
      case 'logoSlider':
        return logoSliderBarWidget(block).map(blocksStateUpdaters.logoSliderBlockUpdater)
      case 'linkGridBlockNew':
        return fromJSX(() => <LinkGridBlockLayout {...block} />)
      case 'advertisementBlock':
        return fromJSX(() => <AdvertisementBlockLayout {...block} />)
      case 'noBlock':
        return fromJSX(() => <></>)
    }
  }
